import type { Statemachine } from 'overmind';
import { statemachine } from 'overmind';

type LoadedValue<T> = {
  value: T;
};

type LoadedState<T> = {
  current: 'LOADED';
} & LoadedValue<T>;

type LoadingValue<T> = {
  actionBody: T;
};

type LoadingState<T> = {
  current: 'LOADING';
} & Partial<LoadingValue<T>>;

type States<T, U> =
  | {
      current: 'NOT_LOADED';
    }
  | LoadingState<U>
  | LoadedState<T>
  | {
      current: 'ERROR';
      error: string;
    };

type BaseState<T> = Partial<LoadedValue<T>>;

type Events<T, U> =
  | {
      type: 'FETCH_SUCCESS';
      data: T;
    }
  | {
      type: 'FETCH_ERROR';
      data: string;
    }
  | {
      type: 'TRIGGER_LOADING';
      data: U;
    }
  | {
      type: 'UNLOAD';
    };

export type GenericFetchMachineType<T, U = undefined> = Statemachine<
  States<T, U>,
  Events<T, U>,
  BaseState<T>
>;

export type GenericFetchMachineStates = States<unknown, unknown>['current'];

function GenericFetchMachine<T, U>() {
  return statemachine<States<T, U>, Events<T, U>, BaseState<T>>({
    NOT_LOADED: {
      TRIGGER_LOADING: actionBody => ({
        current: 'LOADING',
        actionBody
      })
    },
    LOADING: {
      FETCH_SUCCESS: value => ({
        current: 'LOADED',
        value
      }),
      FETCH_ERROR: error => ({
        current: 'ERROR',
        error
      }),
      UNLOAD: () => ({
        current: 'NOT_LOADED'
      })
    },
    LOADED: {
      TRIGGER_LOADING: actionBody => ({
        current: 'LOADING',
        actionBody
      }),
      UNLOAD: () => ({
        current: 'NOT_LOADED'
      })
    },
    ERROR: {
      TRIGGER_LOADING: actionBody => ({
        current: 'LOADING',
        actionBody
      }),
      UNLOAD: () => ({
        current: 'NOT_LOADED'
      })
    }
  });
}

export function createGenericFetchMachine<T, U = undefined>(
  initialState?: States<T, U>
) {
  const machine = GenericFetchMachine<T, U>();
  return machine.create(
    { current: (initialState?.current || 'NOT_LOADED') as any },
    {
      value: initialState?.current === 'LOADED' ? initialState.value : undefined
    }
  );
}
